import { z } from 'zod';
import { dayOf } from './filters';
import type { TaskDTO } from './tasks';

// ---------- Time tracking ----------

export interface TimeEntryDTO {
  id: string;
  /** Null for time not tied to a task. */
  taskId: string | null;
  /** Name of the task, for showing the entry away from its task. */
  taskName: string | null;
  userId: string;
  start: number;
  /** Null while the timer is running. */
  end: number | null;
  /** 0 while the timer is running; use `entryDuration` to read a live value. */
  durationMs: number;
  description: string | null;
  billable: boolean;
}

/** Realtime payload of `timer.changed`, sent to every device of the user. */
export interface TimerEventData {
  running: TimeEntryDTO | null;
}

/** Realtime payload of `time.changed`: an entry was added, edited or removed. */
export interface TimeChangedEventData {
  entry: TimeEntryDTO;
  deleted?: boolean;
}

/** Duration of an entry, counting up to `now` while it is still running. */
export function entryDuration(entry: Pick<TimeEntryDTO, 'start' | 'end' | 'durationMs'>, now: number): number {
  return entry.end === null ? Math.max(0, now - entry.start) : entry.durationMs;
}

const id = z.string().min(1).max(64);
const instant = z.number().int().min(0);
const description = z.string().trim().max(500).nullable();
/** Longest single entry: guards against typos like a wrong year. */
const MAX_ENTRY_MS = 7 * 86_400_000;

export const startTimerSchema = z.object({
  taskId: id.nullable().optional(),
  description: description.optional(),
  billable: z.boolean().optional(),
});
export type StartTimerInput = z.infer<typeof startTimerSchema>;

export const createTimeEntrySchema = z
  .object({
    taskId: id.nullable().optional(),
    start: instant,
    /** Give `end`, or `durationMs` (the entry then ends at start + duration). */
    end: instant.optional(),
    durationMs: z.number().int().min(60_000).max(MAX_ENTRY_MS).optional(),
    description: description.optional(),
    billable: z.boolean().optional(),
  })
  .refine((v) => v.end !== undefined || v.durationMs !== undefined, { message: 'Give an end time or a duration' })
  .refine((v) => v.end === undefined || (v.end > v.start && v.end - v.start <= MAX_ENTRY_MS), {
    message: 'The end must be after the start, and within 7 days of it',
  });
export type CreateTimeEntryInput = z.infer<typeof createTimeEntrySchema>;

export const updateTimeEntrySchema = z.object({
  taskId: id.nullable().optional(),
  start: instant.optional(),
  end: instant.optional(),
  durationMs: z.number().int().min(60_000).max(MAX_ENTRY_MS).optional(),
  description: description.optional(),
  billable: z.boolean().optional(),
});
export type UpdateTimeEntryInput = z.infer<typeof updateTimeEntrySchema>;

export const timeEntryQuerySchema = z.object({
  /** Whose entries. Defaults to the caller when no task is given. */
  userId: id.optional(),
  /** Entries on this task. With `subtasks`, on its subtasks too. */
  taskId: id.optional(),
  subtasks: z.enum(['true', 'false']).optional(),
  from: z.coerce.number().int().optional(),
  to: z.coerce.number().int().optional(),
});
export type TimeEntryQuery = z.infer<typeof timeEntryQuerySchema>;

// ---------- Planner time blocks ----------

export interface TimeBlockDTO {
  id: string;
  userId: string;
  taskId: string | null;
  taskName: string | null;
  /** Shown when there is no task, or to override the task name. */
  title: string | null;
  start: number;
  end: number;
  /** Office meetings: shared by every attendee's block. */
  meetingId: string | null;
  floorId: string | null;
  roomId: string | null;
  roomName: string | null;
  organizerId: string | null;
  /** Everyone in the meeting (organizer included). */
  attendeeIds: string[];
}

export const createMeetingSchema = z
  .object({
    floorId: id,
    roomId: id,
    title: z.string().trim().min(1).max(200),
    start: instant,
    end: instant,
    attendeeIds: z.array(id).max(100).default([]),
  })
  .refine((v) => v.end > v.start && v.end - v.start <= 86_400_000, { message: 'A meeting must end after it starts and last at most a day' });
export type CreateMeetingInput = z.infer<typeof createMeetingSchema>;

export const updateMeetingSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  start: instant.optional(),
  end: instant.optional(),
  attendeeIds: z.array(id).max(100).optional(),
});
export type UpdateMeetingInput = z.infer<typeof updateMeetingSchema>;

export interface MeetingDTO {
  id: string;
  floorId: string;
  roomId: string;
  roomName: string | null;
  title: string;
  start: number;
  end: number;
  organizerId: string;
  attendeeIds: string[];
}

export const createTimeBlockSchema = z
  .object({
    taskId: id.nullable().optional(),
    title: z.string().trim().max(200).nullable().optional(),
    start: instant,
    end: instant,
  })
  .refine((v) => v.end > v.start && v.end - v.start <= 86_400_000, {
    message: 'A time block must end after it starts and last at most a day',
  })
  .refine((v) => Boolean(v.taskId) || Boolean(v.title), { message: 'Give the block a task or a title' });
export type CreateTimeBlockInput = z.infer<typeof createTimeBlockSchema>;

export const updateTimeBlockSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  start: instant.optional(),
  end: instant.optional(),
});
export type UpdateTimeBlockInput = z.infer<typeof updateTimeBlockSchema>;

export const timeBlockQuerySchema = z.object({
  from: z.coerce.number().int(),
  to: z.coerce.number().int(),
});

// ---------- Rollups ----------

/**
 * Totals a per-task number over each task and all of its loaded descendants
 * (time tracked and estimates roll up from subtasks).
 */
export function rollup(tasks: TaskDTO[], pick: (task: TaskDTO) => number | null | undefined): Map<string, number> {
  const children = new Map<string, TaskDTO[]>();
  for (const task of tasks) {
    if (!task.parentId) continue;
    const list = children.get(task.parentId);
    if (list) list.push(task);
    else children.set(task.parentId, [task]);
  }
  const totals = new Map<string, number>();
  const visit = (task: TaskDTO, seen: Set<string>): number => {
    const known = totals.get(task.id);
    if (known !== undefined) return known;
    if (seen.has(task.id)) return 0;
    seen.add(task.id);
    let total = pick(task) ?? 0;
    for (const child of children.get(task.id) ?? []) total += visit(child, seen);
    totals.set(task.id, total);
    return total;
  };
  for (const task of tasks) visit(task, new Set());
  return totals;
}

// ---------- Workload ----------

export const WORKLOAD_MEASURES = ['hours', 'count', 'points'] as const;
export type WorkloadMeasure = (typeof WORKLOAD_MEASURES)[number];

/** Default capacity per person per working day, by measure. */
export const DEFAULT_CAPACITY: Record<WorkloadMeasure, number> = { hours: 8, count: 3, points: 5 };

const DAY = 86_400_000;

export function isWeekend(day: number): boolean {
  const dow = new Date(day).getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * The calendar days (UTC midnight convention) a task occupies: start through due, or the single
 * day it has. Empty when the task has no dates. Weekends are skipped unless the task falls only
 * on weekend days, so no load is ever lost.
 */
export function taskDays(
  task: Pick<TaskDTO, 'startDate' | 'startHasTime' | 'dueDate' | 'dueHasTime'>,
  includeWeekends = false,
): number[] {
  const due = task.dueDate === null ? null : dayOf(task.dueDate, task.dueHasTime);
  const start = task.startDate === null ? null : dayOf(task.startDate, task.startHasTime);
  const from = start ?? due;
  const to = due ?? start;
  if (from === null || to === null) return [];
  const first = Math.min(from, to);
  // Cap very long spans so a mistyped year cannot build a huge array.
  const last = Math.min(Math.max(from, to), first + 365 * DAY);
  const all: number[] = [];
  for (let day = first; day <= last; day += DAY) all.push(day);
  if (includeWeekends) return all;
  const weekdays = all.filter((d) => !isWeekend(d));
  return weekdays.length ? weekdays : all;
}

/** How much one task weighs in total for a measure: estimated hours, 1, or points. */
export function taskLoad(task: Pick<TaskDTO, 'timeEstimateMs' | 'points'>, measure: WorkloadMeasure): number {
  if (measure === 'count') return 1;
  if (measure === 'points') return task.points ?? 0;
  return (task.timeEstimateMs ?? 0) / 3_600_000;
}

/**
 * Spreads a task's load over its days. Hours and points are split evenly; with `count` the task
 * counts as one on every day it spans. A task with several assignees weighs the same for each.
 */
export function distributeLoad(
  task: Pick<TaskDTO, 'startDate' | 'startHasTime' | 'dueDate' | 'dueHasTime' | 'timeEstimateMs' | 'points'>,
  measure: WorkloadMeasure,
  includeWeekends = false,
): Map<number, number> {
  const days = taskDays(task, includeWeekends);
  const total = taskLoad(task, measure);
  const perDay = measure === 'count' ? 1 : days.length ? total / days.length : 0;
  return new Map(days.map((d) => [d, perDay]));
}
